// Interactive preview (7.6D): plays the canonical document of one format with
// the deterministic PreviewEngine — animations sampled by the Creative
// Engine's timeline function, interactions run locally, scene transitions.
// One requestAnimationFrame loop advances logical time; per-frame values are
// written to the DOM imperatively; React re-renders only on discrete changes
// (scene, show/hide, variables, new preview events).
//
// This is NOT the Delivery Runtime (7.6H): nothing is sent anywhere, there is
// no ad instance and no impression. The event list shows what WOULD be
// measured; none of it is billable.
//
// 7.6E: video/audio, carousel, survey and form run here too. Media elements
// follow the engine (syncMedia) — the engine never reads them back. Autoplay
// can be checked under a simulated "blocks autoplay" browser policy; when the
// authored intent and the simulated outcome differ, the preview says so.
import { RUNNER_MAX_CATCH_UP_MS, ease, type CreativeDocument } from "@interactads/creative-model";
import { ArrowDown, ArrowUp, Dices, Eye, Gamepad2, Info, Pause, Play, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { elementLabel, findLayout, findScene, layoutSize, sceneLabel } from "../core/model";
import { PreviewEngine, type GameNotice, type PreviewEvent, type SceneTransitionState } from "../preview/engine";
import { animatedTargets, applySampledStyles } from "../render/animate";
import { Artboard } from "../render/artboard";
import { documentRuntimeGaps, type RuntimeGap } from "@interactads/creative-runtime/support";
import { gameNoticeText as sharedGameNoticeText } from "@interactads/creative-runtime/ui-text";
import { countdownText, plain, syncCountdowns, syncMedia, type ComponentHost } from "../render/components";
import { paintRunner } from "../render/runner-paint";
import { useStudio } from "./context";
import { IsolatedHtml5Preview } from "./html5-preview";
import { formatSeconds } from "./playback";

function transitionStyles(tr: SceneTransitionState, now: number): { incoming: CSSProperties; outgoing: CSSProperties } {
  const p = ease(tr.easing, Math.min(1, Math.max(0, (now - tr.start) / tr.duration)), tr.duration);
  if (tr.kind === "fade") return { incoming: { opacity: p }, outgoing: {} };
  const axis = tr.direction === "left" || tr.direction === "right" ? "X" : "Y";
  const sign = tr.direction === "left" || tr.direction === "up" ? 1 : -1;
  const incoming = { transform: `translate${axis}(${String(sign * (1 - p) * 100)}%)` };
  const outgoing = tr.kind === "push" ? { transform: `translate${axis}(${String(-sign * p * 100)}%)` } : {};
  return { incoming, outgoing };
}

const CONTROL_LABEL: Record<string, string> = {
  play: "reproducir",
  pause: "pausar",
  mute: "silenciar",
  unmute: "activar sonido",
  replay: "repetir",
};
const VIA_LABEL: Record<string, string> = { arrow: "flecha", dot: "punto", swipe: "deslizar", rule: "interacción" };
const KIND_LABEL: Record<string, string> = { hover: "puntero", tap: "toque", drag: "arrastre", drop: "soltar" };
export const OUTCOME_LABEL: Record<string, string> = {
  win: "ganó",
  lose: "perdió",
  timeout: "se acabó el tiempo",
  neutral: "terminado",
};

/** Scalar attribute as text (lists/maps are formatted where they occur). */
const str = (v: unknown) => (typeof v === "string" || typeof v === "number" ? String(v) : "");

const PHASE_LABEL = {
  idle: "esperando",
  running: "en curso",
  paused: "en pausa (fuera de sus escenas)",
  ended: "terminado",
};

/** Author-facing status line (rewritten every frame for the time; not announced). */
function gameStatusText(e: PreviewEngine): string {
  const st = e.gameStatus();
  const game = e.game;
  if (!st || !game) return "";
  const g = game.decl;
  const parts = [`Juego: ${PHASE_LABEL[st.phase]}${st.outcome ? ` (${OUTCOME_LABEL[st.outcome] ?? st.outcome})` : ""}`];
  const v = (id: string | undefined) => (id === undefined ? undefined : e.variable(id));
  if (g.score !== undefined) parts.push(`puntos ${String(v(g.score) ?? 0)}`);
  if (g.lives !== undefined) parts.push(`vidas ${String(v(g.lives) ?? 0)}`);
  if (g.timer !== undefined) parts.push(`tiempo ${countdownText(e.timerRemaining(g.timer), "mm:ss")}`);
  const total = game.total();
  if (total > 0) parts.push(`objetivos ${String(total - game.remaining())}/${String(total)}`);
  const r = game.runner;
  if (r) {
    if (r.decl.energy) parts.push(`energía ${String(v(r.decl.energy.var) ?? 0)}`);
    const seg = r.decl.segments[r.segmentIndex];
    parts.push(
      `escenario ${String(r.segmentIndex + 1)}/${String(r.decl.segments.length)}${seg?.name ? ` (${seg.name})` : ""}`
    );
    parts.push(`recorrido ${String(Math.min(100, Math.round((r.time / Math.max(1, r.courseMs)) * 100)))} %`);
    parts.push(`choques ${String(e.runnerStats.hazards)} · recogidos ${String(e.runnerStats.pickups)}`);
  }
  return parts.join(" · ");
}

function describeEvent(doc: CreativeDocument, e: PreviewEvent): string {
  const a = e.attrs;
  const el = (id: unknown) => {
    const found = doc.elements.find((x) => x.id === id);
    return found ? `«${elementLabel(found)}»` : "un elemento";
  };
  const scene = (id: unknown) => {
    const s = typeof id === "string" ? findScene(doc, id) : undefined;
    return s ? `«${sceneLabel(doc, s)}»` : "una escena";
  };
  const question = (id: unknown) => {
    const q = doc.survey?.questions.find((x) => x.id === id);
    return q ? `«${plain(q.prompt)}»` : "una pregunta";
  };
  const field = (form: unknown, id: unknown) => {
    const f = doc.forms?.find((x) => x.id === form)?.fields.find((x) => x.id === id);
    return f ? `«${plain(f.label)}»` : "una pregunta";
  };
  switch (e.type) {
    case "scene_view":
      return `Vista de escena ${scene(a.scene)}`;
    case "cta_click":
      return `Clic al destino ${doc.links.find((l) => l.id === a.link)?.url ?? ""}`;
    case "element_interaction":
      return `Interacción (${KIND_LABEL[str(a.kind)] ?? "toque"}) con ${el(a.el)}`;
    case "engagement":
      return "Primera interacción";
    case "custom": {
      const { name, ...props } = a;
      const decl = doc.events?.find((x) => x.name === name);
      const extra = Object.entries(props)
        .map(([k, v]) => `${k}: ${str(v)}`)
        .join(", ");
      return `Evento propio «${decl?.label ?? str(name)}»${extra ? ` (${extra})` : ""}`;
    }
    case "video_start":
      return `Empezó el video ${el(a.el)}`;
    case "video_25":
    case "video_50":
    case "video_75":
      return `Video ${el(a.el)} al ${e.type.slice(6)} %`;
    case "video_complete":
      return `Video ${el(a.el)} completo`;
    case "audio_start":
      return `Empezó el audio ${el(a.el)}`;
    case "audio_complete":
      return `Audio ${el(a.el)} completo`;
    case "media_control":
      return `Control de ${a.media === "audio" ? "audio" : "video"}: ${CONTROL_LABEL[str(a.action)] ?? str(a.action)}`;
    case "carousel_slide_view":
      return `Diapositiva ${String(Number(a.index) + 1)} vista${a.cause === "auto" ? "" : " (por la persona)"}`;
    case "carousel_navigate":
      return `Carrusel a la diapositiva ${String(Number(a.index) + 1)} (${VIA_LABEL[str(a.via)] ?? str(a.via)})`;
    case "form_start":
      return "Empezó el formulario";
    case "form_interaction":
      return `Respondió ${field(a.form, a.field)}`;
    case "form_submit":
      return a.outcome === "valid"
        ? `Formulario enviado${Array.isArray(a.choices) ? ` · opciones: ${a.choices.join(", ")}` : ""}${
            a.ratings && typeof a.ratings === "object" && !Array.isArray(a.ratings)
              ? ` · valoraciones: ${Object.entries(a.ratings)
                  .map(([k, v]) => `${k}=${String(v)}`)
                  .join(", ")}`
              : ""
          }`
        : "Intento de envío con preguntas sin responder";
    case "survey_start":
      return "Empezó la encuesta";
    case "survey_question_view":
      return `Pregunta ${str(a.pos)} vista: ${question(a.q)}`;
    case "answer":
      return `Respuesta a ${question(a.q)}: ${Array.isArray(a.opts) ? a.opts.join(", ") : str(a.value)}`;
    case "survey_complete":
      return `Encuesta completada (${str(a.pathLength)} preguntas)`;
    case "game_start":
      return "Empezó el juego";
    case "game_interaction":
      return `Jugadas: ${str(a.n)} ${a.kind === "drag" ? "arrastre(s)" : "toque(s)"} (agregado)`;
    case "game_complete":
      return `Fin del juego: ${OUTCOME_LABEL[str(a.outcome)] ?? str(a.outcome)}${a.score !== undefined ? ` · puntos ${str(a.score)}` : ""}`;
    case "restart":
      return "Jugar de nuevo";
    case "runtime_error":
      return a.code === "RULE_BUDGET"
        ? "Se cortó una cadena de acciones demasiado larga (límite de seguridad)"
        : "Se cortó una cascada de interacciones demasiado profunda (límite de seguridad)";
  }
}

/** Next session seed: a fixed sequence (Park–Miller), so "another round" is still reproducible from its number. */
export const nextSeed = (seed: number) => (Math.max(1, Math.trunc(seed)) * 48_271) % 2_147_483_647;

/** The announcements are the served ad's own (one text for both, 7.6I). */
export const gameNoticeText = (e: PreviewEngine, n: GameNotice): string => sharedGameNoticeText(e, n);

const GAP_LABEL: Record<string, string> = {
  move: "movimiento automático",
  collider: "colisiones genéricas",
  "follow-pointer": "seguir al puntero",
  "tappable-target": "zonas de toque ampliadas",
  spawn: "objetos generados por reglas",
  despawn: "objetos generados por reglas",
  "collision.enter": "colisiones genéricas",
  "collision.exit": "colisiones genéricas",
  responsive: "un tamaño adaptable",
};

/** Plain-language list of what the served runtime does not execute (never internal codes). */
function describeGaps(gaps: readonly RuntimeGap[]): string {
  const names = new Set(
    gaps.map((g) => (g.kind === "responsive_layout" ? GAP_LABEL.responsive : (GAP_LABEL[g.what] ?? "una capacidad")))
  );
  return [...names].join(", ");
}

export function InteractivePreview({
  doc,
  ...props
}: {
  doc: CreativeDocument;
  layoutId: string;
  scale: number;
  compact?: boolean;
  bare?: boolean;
}) {
  return doc.html5 ? (
    <IsolatedHtml5Preview project={doc.html5} scale={props.scale} fallback={<Html5BlockedPreview />} />
  ) : (
    <CanonicalInteractivePreview doc={doc} {...props} />
  );
}

export function Html5BlockedPreview() {
  return (
    <div role="status" className="grid gap-2 p-5 text-sm">
      <h2 className="font-semibold">HTML5 pendiente de validación</h2>
      <p>
        El proyecto se conserva en esta versión. La vista previa necesita una autorización vigente del origen aislado.
      </p>
      <p className="text-muted">
        Las modificaciones se solicitan por chat. El editor de capas no modifica archivos HTML5.
      </p>
    </div>
  );
}

function CanonicalInteractivePreview({
  doc,
  layoutId,
  scale,
  compact = false,
  bare = false,
}: {
  doc: CreativeDocument;
  layoutId: string;
  scale: number;
  /** Inline in the AI-first Studio: the ad and its controls only (no measurement list, no simulation toggles). */
  compact?: boolean;
  /** A thumbnail (the prebuilt gallery, report 109): the ad alone, without controls, status or measurement. */
  bare?: boolean;
}) {
  const ctx = useStudio();
  const [run, setRun] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [showHotspots, setShowHotspots] = useState(false);
  const [blockAutoplay, setBlockAutoplay] = useState(false);
  const [seed, setSeed] = useState(1);
  const [, setVersion] = useState(0);
  const incomingRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const statusRef = useRef<HTMLSpanElement>(null);
  const layout = findLayout(doc, layoutId);
  const size = layout ? layoutSize(layout) : { width: 1, height: 1 };
  const targets = useMemo(() => animatedTargets(doc, layoutId), [doc, layoutId]);

  const engine = useMemo(() => {
    const e = new PreviewEngine(doc, layoutId, {
      seed,
      autoplay: blockAutoplay ? "block" : "allow-muted",
      onLink: (_id, url) => {
        toast(url ? `Este clic abrirá ${url}` : "Este clic no tiene destino definido.");
      },
    });
    e.start();
    return e;
    // `run` restarts the preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, layoutId, run, blockAutoplay, seed]);
  const stageRef = useRef<HTMLDivElement>(null);
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);
  const paint = useCallback(() => {
    const e = engine;
    const sceneEnd = e.currentScene().duration;
    if (incomingRef.current)
      applySampledStyles(incomingRef.current, targets, e.sceneTime(), "preview", {
        sceneEnd,
        manualStarts: e.manualStarts(),
      });
    const tr = e.transition;
    if (tr) {
      const from = findScene(e.doc, tr.from);
      if (outgoingRef.current)
        applySampledStyles(outgoingRef.current, targets, tr.fromTime, "preview", { sceneEnd: from?.duration ?? null });
      const styles = transitionStyles(tr, e.now);
      const inner = incomingRef.current?.parentElement;
      if (inner) Object.assign(inner.style, { opacity: "", transform: "" }, styles.incoming);
      const outer = outgoingRef.current?.parentElement;
      if (outer) Object.assign(outer.style, { transform: "" }, styles.outgoing);
    } else {
      const inner = incomingRef.current?.parentElement;
      if (inner) Object.assign(inner.style, { opacity: "", transform: "" });
    }
    if (timeRef.current)
      timeRef.current.textContent = `${sceneLabel(e.doc, e.currentScene())} · ${formatSeconds(e.sceneTime())}`;
    paintRunner(incomingRef.current, e.runner);
    syncMedia(stageRef.current, e, playingRef.current);
    syncCountdowns(stageRef.current, e);
    if (statusRef.current) statusRef.current.textContent = gameStatusText(e);
  }, [engine, targets]);

  // One rAF loop; React only hears about discrete changes.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    let seen = engine.version;
    const step = (now: number) => {
      // Bounded catch-up: a long frame (stall, background tab) slows the game, never skips simulation.
      engine.advance(Math.min(RUNNER_MAX_CATCH_UP_MS, now - last));
      last = now;
      if (engine.version !== seen) {
        seen = engine.version;
        setVersion(seen);
      }
      paint();
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [engine, playing, paint]);
  useLayoutEffect(paint);

  // Advanced components: the engine's own API, re-rendering after each input (also while paused).
  const host = useMemo<ComponentHost>(() => {
    const after =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        fn(...args);
        setVersion(engine.version);
      };
    return {
      mediaView: (id) => engine.mediaView(id),
      mediaControl: after((id, action) => {
        engine.mediaControl(id, action);
      }),
      carouselView: (id) => engine.carouselView(id),
      carouselNavigate: after((id, to, via) => {
        engine.carouselNavigate(id, to, via);
      }),
      carouselHold: after((id, held) => {
        engine.carouselHold(id, held);
      }),
      carouselTogglePause: after((id) => {
        engine.carouselTogglePause(id);
      }),
      surveyView: () => engine.surveyView(),
      surveyChoose: after((choice) => {
        engine.surveyChoose(choice);
      }),
      surveyNext: after(() => {
        engine.surveyNext();
      }),
      surveyCanContinue: () => engine.surveyCanContinue(),
      fieldValue: (form, field) => engine.fieldValue(form, field),
      fieldError: (form, field) => engine.fieldError(form, field),
      fieldInput: after((id, value) => {
        engine.fieldInput(id, value);
      }),
      timerRemaining: (id) => engine.timerRemaining(id),
      variable: (id) => engine.variable(id),
    };
  }, [engine]);

  // Pausing the preview pauses the media elements too (the frame loop stops).
  useEffect(() => {
    if (!playing) syncMedia(stageRef.current, engine, false);
  }, [playing, engine]);

  const input = useMemo(
    () => ({
      tappable: (id: string) => engine.isTappable(id),
      hoverable: (id: string) => engine.hasHover(id),
      tap: (id: string) => {
        engine.tap(id);
        setVersion(engine.version);
      },
      hover: (id: string, enter: boolean) => {
        engine.hover(id, enter);
        setVersion(engine.version);
      },
      draggable: (id: string) => engine.isDraggable(id),
      dragStart: (id: string) => engine.dragStart(id),
      dragPosition: (id: string, x: number, y: number) => engine.dragPosition(id, x, y),
      dragEnd: (id: string, x: number, y: number) => {
        engine.dragEnd(id, x, y);
        setVersion(engine.version);
      },
      pick: (id: string) => {
        engine.pick(id);
        setVersion(engine.version);
      },
      cancelPick: () => {
        engine.cancelPick();
        setVersion(engine.version);
      },
      // Runner controls: the jump control acts on press; the duck control while held.
      press: (id: string, down: boolean) => {
        const c = engine.runner?.decl.controls;
        if (c?.jump === id && down) engine.runnerJump();
        if (c?.duck === id) engine.runnerDuck(down);
        setVersion(engine.version);
      },
    }),
    [engine]
  );
  const runnerKeys = (ev: React.KeyboardEvent, down: boolean) => {
    // Only while a runner round is active and the focus is inside the preview: never browser shortcuts.
    if (!engine.runnerActive() || ev.altKey || ev.ctrlKey || ev.metaKey) return;
    if (ev.key === " " || ev.key === "ArrowUp") {
      ev.preventDefault();
      if (down && !ev.repeat) engine.runnerJump();
    } else if (ev.key === "ArrowDown") {
      ev.preventDefault();
      if (!ev.repeat) engine.runnerDuck(down);
    } else return;
    setVersion(engine.version);
  };
  const runtime = {
    stateOf: (id: string) => engine.stateOf(id),
    positionOf: (id: string) => engine.positionOf(id),
    gameView: (id: string) => engine.gameView(id),
    picked: engine.picked,
    runner: engine.runner?.decl ?? null,
  };

  const tr = engine.transition;
  const events = engine.events.slice(-12).reverse();
  // What the SERVED ad will do with this format (same check as the delivery runtime, ADR-0029).
  const servedGaps = documentRuntimeGaps(doc, layoutId);
  const board = (sceneId: string, ref: React.RefObject<HTMLDivElement | null>, live: boolean) => (
    <Artboard
      doc={doc}
      layoutId={layoutId}
      sceneId={sceneId}
      boardNode={ref}
      assetUrl={ctx.assetUrl}
      loadedFonts={ctx.loadedFonts}
      mode="preview"
      visibility={engine.visibilityOverrides()}
      variables={engine.variables()}
      showHotspots={showHotspots}
      host={host}
      runtime={runtime}
      {...(live ? { interact: input } : {})}
    />
  );
  const notice = engine.gameLog.at(-1);
  const gameState = engine.gameStatus();
  const outcomes = doc.elements.flatMap((el) => {
    if (el.type !== "video" && el.type !== "audio") return [];
    const v = engine.mediaView(el.id);
    if (!v?.outcome) return [];
    const name = `«${elementLabel(el)}»`;
    return [
      v.outcome === "autoplay_blocked"
        ? `${name}: el navegador simulado bloqueó la reproducción automática; se muestra un botón para reproducir.`
        : `${name}: pediste sonido al empezar, pero los navegadores solo permiten sonido después de que la persona toque. Empieza sin sonido.`,
    ];
  });

  return (
    <div className={cn("grid gap-3", !compact && !bare && "lg:grid-cols-[minmax(0,1fr)_16rem]")}>
      <div className="grid gap-2">
        <div className={cn("flex flex-wrap items-center gap-1.5", bare && "hidden")}>
          <Button
            size="sm"
            variant="secondary"
            aria-label={playing ? "Pausar la vista previa" : "Reproducir la vista previa"}
            onClick={() => {
              setPlaying((p) => !p);
            }}
          >
            {playing ? <Pause /> : <Play />} {playing ? "Pausar" : "Reproducir"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-label="Reiniciar la vista previa"
            onClick={() => {
              setRun((r) => r + 1);
              setPlaying(true);
            }}
          >
            <RotateCcw /> Reiniciar
          </Button>
          <span className="text-xs text-muted tabular" aria-live="off" data-testid="preview-time">
            <span ref={timeRef} />
          </span>
          <label className={cn("ml-auto flex items-center gap-1.5 text-xs", compact && "hidden")}>
            <input
              type="checkbox"
              checked={blockAutoplay}
              onChange={(e) => {
                setBlockAutoplay(e.target.checked);
              }}
            />
            Simular bloqueo de reproducción automática
          </label>
          <label className={cn("flex items-center gap-1.5 text-xs", compact && "hidden")}>
            <input
              type="checkbox"
              checked={showHotspots}
              onChange={(e) => {
                setShowHotspots(e.target.checked);
              }}
            />
            <Eye className="size-3.5 text-muted" aria-hidden="true" /> Mostrar zonas clicables
          </label>
        </div>
        <div
          ref={stageRef}
          className={cn(
            "ia-preview-stage grid place-items-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
            bare ? "overflow-hidden" : "min-h-48 overflow-auto rounded-md border border-line p-6"
          )}
          data-testid="preview-stage"
          {...(engine.runner
            ? {
                tabIndex: 0,
                "aria-label": "Juego de carrera: Espacio o flecha arriba para saltar, flecha abajo para agacharse",
                onKeyDown: (ev: React.KeyboardEvent) => {
                  runnerKeys(ev, true);
                },
                onKeyUp: (ev: React.KeyboardEvent) => {
                  runnerKeys(ev, false);
                },
              }
            : {})}
        >
          <div
            className="ia-preview-artboard ia-checker"
            style={{ width: size.width * scale, height: size.height * scale }}
          >
            <div
              style={{
                transform: `scale(${String(scale)})`,
                transformOrigin: "0 0",
                width: size.width,
                height: size.height,
                position: "relative",
                overflow: "hidden",
              }}
              data-preview-scene={engine.scene}
            >
              {tr ? (
                <div className="absolute inset-0" aria-hidden="true">
                  {board(tr.from, outgoingRef, false)}
                </div>
              ) : null}
              <div className={cn("absolute inset-0")}>{board(engine.scene, incomingRef, true)}</div>
            </div>
          </div>
        </div>
        {outcomes.length > 0 && !bare ? (
          <ul
            className="grid gap-1 rounded-md bg-info-soft p-2 text-2xs text-info"
            aria-label="Intención y resultado de la reproducción"
            data-testid="media-outcomes"
          >
            {outcomes.map((o) => (
              <li key={o} className="flex gap-1.5">
                <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                {o}
              </li>
            ))}
          </ul>
        ) : null}
        {gameState && !bare ? (
          <div
            className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-sunken px-2.5 py-1.5 text-2xs"
            data-testid="preview-game"
          >
            <Gamepad2 className="size-3.5 text-muted" aria-hidden="true" />
            <span ref={statusRef} className="tabular" data-testid="preview-game-status">
              {gameStatusText(engine)}
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <label className="flex items-center gap-1 text-muted">
                Partida nº
                <input
                  type="number"
                  min={1}
                  className="h-6 w-20 rounded-sm border border-line bg-surface px-1 text-2xs tabular"
                  value={seed}
                  aria-label="Número de partida (semilla para reproducirla)"
                  onChange={(ev) => {
                    const v = Math.trunc(Number(ev.target.value));
                    if (Number.isFinite(v) && v >= 1) setSeed(v);
                  }}
                />
              </label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-2xs"
                onClick={() => {
                  setSeed((x) => nextSeed(x));
                  setPlaying(true);
                }}
              >
                <Dices /> Otra partida
              </Button>
            </span>
            {engine.runner ? (
              <span className="flex basis-full flex-wrap items-center gap-1.5" data-testid="runner-controls">
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7"
                  disabled={!engine.runnerActive()}
                  onPointerDown={(ev) => {
                    ev.preventDefault();
                    engine.runnerJump();
                    setVersion(engine.version);
                  }}
                  onKeyDown={(ev) => {
                    if (ev.key !== "Enter" && ev.key !== " ") return;
                    ev.preventDefault();
                    engine.runnerJump();
                    setVersion(engine.version);
                  }}
                >
                  <ArrowUp /> Saltar
                </Button>
                {engine.runner.decl.player.duck ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7"
                    disabled={!engine.runnerActive()}
                    onPointerDown={(ev) => {
                      ev.preventDefault();
                      ev.currentTarget.setPointerCapture(ev.pointerId);
                      engine.runnerDuck(true);
                      setVersion(engine.version);
                    }}
                    onPointerUp={() => {
                      engine.runnerDuck(false);
                    }}
                    onPointerCancel={() => {
                      engine.runnerDuck(false);
                    }}
                    onKeyDown={(ev) => {
                      if ((ev.key !== "Enter" && ev.key !== " ") || ev.repeat) return;
                      ev.preventDefault();
                      engine.runnerDuck(true);
                      setVersion(engine.version);
                    }}
                    onKeyUp={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") engine.runnerDuck(false);
                    }}
                  >
                    <ArrowDown /> Agacharse
                  </Button>
                ) : null}
                <span className="text-subtle">
                  O con el teclado dentro del área del juego: Espacio / ↑ salta, ↓ (mantener) se agacha.
                </span>
              </span>
            ) : null}
            <p className="basis-full text-subtle">
              La misma partida nº se juega igual (cartas mezcladas, saltos al azar, peligros que aparecen): úsala para
              reproducir un caso. Con teclado: Tab hasta una pieza, Enter para tomarla y Enter sobre su caja para
              soltarla.
            </p>
          </div>
        ) : null}
        <p className="sr-only" aria-live="polite" data-testid="preview-game-notice">
          {notice ? gameNoticeText(engine, notice) : ""}
        </p>
        {!bare && (servedGaps.length > 0 || engine.unsupported.size > 0) ? (
          <p className="text-2xs text-muted" data-testid="served-fallback-notice">
            {servedGaps.length > 0
              ? `En este formato el anuncio publicado todavía no puede mostrarse: usa ${describeGaps(servedGaps)}, que el anuncio servido no ejecuta todavía.`
              : "Este creativo usa interacciones que la vista previa no simula."}{" "}
            La carrera del juego sí se simula y se sirve.
          </p>
        ) : null}
      </div>
      {compact || bare ? null : (
        <aside
          aria-label="Medición en la vista previa"
          className="grid content-start gap-2 rounded-md border border-line p-2.5"
        >
          <h3 className="text-2xs font-semibold uppercase tracking-wide text-muted">Lo que se mediría</h3>
          <ol className="grid gap-1 text-2xs" aria-live="polite" data-testid="preview-events">
            {events.map((e, i) => (
              <li key={`${String(e.t)}-${String(i)}`} className="flex gap-1.5">
                <span className="w-9 shrink-0 text-muted tabular">{(e.t / 1_000).toFixed(1)}s</span>
                <span>{describeEvent(doc, e)}</span>
              </li>
            ))}
          </ol>
          <p className="text-2xs text-subtle">
            Vista previa: no se envía nada. Las interacciones son métricas y nunca facturan; solo la carga del anuncio
            (creative_loaded) cuenta como impresión.
          </p>
        </aside>
      )}
    </div>
  );
}
